import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { EntityIcon } from "../map/EntityIcon.tsx"
import { selectActiveLayers, toggleLayer } from "../redux/global-map.slice"
import type { EntityType } from "../redux/global-map.types"

// Order matches the reference mockup: Lead (pin, left) then Project (house,
// right) — not the "Projects & Leads" label's own word order.
const LAYER_TOGGLE_ORDER: EntityType[] = ["lead", "project"]

/**
 * Replaces the "Projects & Leads" `FacetMultiSelectFilter` dropdown with two
 * independent pill toggle buttons, per explicit product direction and a
 * reference mockup showing exactly this look: a "Lead" pill and a "Project"
 * pill sharing one light-blue rounded backdrop, each carrying the same
 * `EntityIcon` (Leads/Projects KPI icon+color pair) every other Global Map
 * surface uses, each independently on/off, both on by default. Dispatches
 * the same
 * `toggleLayer` action (and reads the same `activeLayers` state,
 * `selectActiveLayers`) the retired standalone `LayerSelector` pill pair and
 * the dropdown it replaced both used — only the visual presentation and the
 * dropped facet counts change, never the underlying 0/1/2-choice semantics
 * ("zero is a valid, allowed selection" — see `toggleLayer`'s own comment in
 * `global-map.slice.ts`).
 *
 * `aria-pressed` + explicit Enter/Space `onKeyDown` handling follows the
 * `LayerSelector` precedent this supersedes: a native `<button>` already
 * turns a focused Enter/Space into a click in a real browser, but jsdom does
 * not simulate that default action, so the handler drives the toggle
 * directly (with `preventDefault` so a real keypress cannot double-toggle).
 */
export function LayerToggleButtons() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const activeLayers = useAppSelector(selectActiveLayers)

  const toggle = (entityType: EntityType) => {
    dispatch(toggleLayer(entityType))
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    entityType: EntityType
  ) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Spacebar"
    ) {
      event.preventDefault()
      toggle(entityType)
    }
  }

  return (
    <div
      role="group"
      aria-label={t("globalMap.filters.projectsAndLeads")}
      className="flex h-9 shrink-0 items-center gap-1 rounded-full bg-sky-100 px-1"
      data-testid="global-map-layer-toggle-group"
    >
      {LAYER_TOGGLE_ORDER.map((entityType) => {
        const pressed = activeLayers.includes(entityType)
        const label = t(`globalMap.entityLabel.${entityType}`)
        return (
          <button
            key={entityType}
            type="button"
            aria-pressed={pressed}
            onClick={() => toggle(entityType)}
            onKeyDown={(event) => handleKeyDown(event, entityType)}
            className={cn(
              "flex h-7 cursor-pointer items-center gap-1.5 rounded-full pl-1 pr-3 text-xs font-medium transition-colors",
              pressed
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 opacity-70 hover:opacity-100"
            )}
          >
            <EntityIcon entityType={entityType} className="size-5" />
            {label}
          </button>
        )
      })}
    </div>
  )
}

export default LayerToggleButtons
